// Copyright (c) 2025 Annadaneshwar Yadawad. All rights reserved.

import { useState } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import Paywall from '../components/Paywall';
import { useSubscription } from '../components/SubscriptionContext';
import './Services.css';

const aiFeatures = [
  'Text → Speech voiceover',
  'Speech → Text transcription',
  'Face detection & image analysis',
  'Single ₹5 unlock for all AI services',
];

// Escape user text so the SSML payload is always valid XML.
const escapeSsml = (value = '') =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const TextToSpeech = () => {
  const { user } = useAuth0();
  const email = user?.email;
  const { isSubscribedForEmail, isFeatureUnlocked, subscribeForFeature } = useSubscription();
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const speechRegion = process.env.REACT_APP_SPEECH_REGION || 'eastus';

  const isSubscribed = isSubscribedForEmail(email) || isFeatureUnlocked('ai');

  if (!isSubscribed) {
    return (
      <div className="service-page">
        <Paywall
          title="Unlock AI Services"
          description="Subscribe for ₹5 to access speech and vision tools."
          onSubscribe={() => subscribeForFeature('ai')}
          ctaLabel="Subscribe for ₹5"
          priceLabel="₹5"
          features={aiFeatures}
        />
      </div>
    );
  }

  const handleSynthesize = async () => {
    if (!text.trim()) {
      alert("Please enter some text to synthesize.");
      return;
    }

    try {
      setError('');
      setLoading(true);

      const ssml = `<speak version="1.0" xml:lang="en-US"><voice xml:lang="en-US" xml:gender="Female" name="en-US-JennyNeural">${escapeSsml(text)}</voice></speak>`;

      const response = await axios.post(
        `https://${speechRegion}.tts.speech.microsoft.com/cognitiveservices/v1`,
        ssml,
        {
          headers: {
            'Content-Type': 'application/ssml+xml',
            'X-Microsoft-OutputFormat': 'audio-16khz-32kbitrate-mono-mp3',
            'Ocp-Apim-Subscription-Key': process.env.REACT_APP_SPEECH_KEY,
            'Ocp-Apim-Subscription-Region': speechRegion,
            'User-Agent': 'annadaneshwar-tts-spa',
          },
          responseType: 'blob',
          validateStatus: status => status < 500, // surface 4xx payloads
        }
      );

      const blob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);

      audio.onended = () => setLoading(false);
      try {
        audio.play();
      } catch (err) {
        setLoading(false);
        // Optionally alert or log the error
        console.warn('Audio play() failed:', err);
      }

      if (response.status >= 400) {
        throw new Error(`Synthesis failed (${response.status}). Check subscription key/region and CORS.`);
      }

    } catch (error) {
      setError("Failed to synthesize speech. Please try again.");
      alert("Failed to synthesize speech. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="service-page ai-tts">
      <div className="service-heading">
        <div>
          <h1>Text to Speech</h1>
          <p className="service-description">Turn your text into natural voice with Azure Speech.</p>
        </div>
        <div className="service-actions">
          <span className="action-chip">AI Services</span>
          <span className="action-chip">Text → Speech</span>
        </div>
      </div>

      <div className="service-grid">
        <div className="service-form">
          <label>
            Enter text
            <textarea
              rows={4}
              onChange={e => setText(e.target.value)}
              placeholder="Enter text to convert to speech..."
            />
          </label>
          <button
            onClick={handleSynthesize}
            className="primary-btn"
            disabled={loading}
          >
            {loading ? 'Converting…' : 'Convert to Speech'}
          </button>
          {loading && (
            <div className="status-banner">
              <span className="status-icon check">★</span>
              <span>Generating voice…</span>
            </div>
          )}
          {error && (
            <div className="status-banner error">
              <span className="status-icon error">!</span>
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="analysis-card highlight">
          <p className="analysis-label">Playback</p>
          <p className="analysis-sub">Audio will play automatically after synthesis.</p>
          <p className="analysis-sub">Voice: en-US-JennyNeural · 16kHz mono</p>
        </div>
      </div>
    </div>
  );
};

export default TextToSpeech;